import { audio } from '../lib/audio.js';
import { angleDiff, collidesWalls, dist } from '../lib/geometry.js';
import type { AbilityName, World } from '../world/types.js';
import { STATE } from '../world/types.js';
import { findHackTarget, hackCamera } from './cameras.js';
import { spawnDecoyTrail, spawnDust, spawnSparks } from './particles.js';
import { recordEvent } from './score.js';

export const COOLDOWN_MAX: Readonly<Record<AbilityName, number>> = {
  box: 0.5,
  knock: 1.5,
  decoy: 4,
  takedown: 0.4,
  hack: 4,
};

/** Reach radius for the takedown grab (px). */
export const TAKEDOWN_REACH = 26;
/** Minimum angle (rad) between guard's facing direction and the player for
 * a takedown to land. Guards facing away (player ≥ 90° off-axis) are fair
 * game; in chase, only a near-direct rear approach qualifies. */
export const TAKEDOWN_BEHIND_ANGLE = Math.PI * 0.5;
export const TAKEDOWN_BEHIND_ANGLE_CHASE = Math.PI * 0.7;

export interface TakedownOpportunity {
  /** Guard that would be K.O.'d if takedown is pressed right now. */
  guard: import('../world/types.js').Guard;
  /** True if the guard is in chase — visual feedback may want to flag the
   * "clutch" variant differently. */
  chase: boolean;
}

/**
 * Returns the guard the player is currently positioned to take down, or
 * null if no opportunity exists. Pure read — no mutation. The renderer
 * uses this each frame to draw a green outline cue.
 */
export function findTakedownTarget(world: World): TakedownOpportunity | null {
  const { player } = world;
  if (player.inBox) return null;
  for (const g of world.guards) {
    if (g.ko) continue;
    if (dist(g, player) >= TAKEDOWN_REACH) continue;
    const angToPlayer = Math.atan2(player.y - g.y, player.x - g.x);
    const diff = Math.abs(angleDiff(angToPlayer, g.dir));
    const threshold = g.state === 'chase' ? TAKEDOWN_BEHIND_ANGLE_CHASE : TAKEDOWN_BEHIND_ANGLE;
    if (diff >= threshold) return { guard: g, chase: g.state === 'chase' };
  }
  return null;
}

/** Push a sound event the guards can perceive and the renderer can animate. */
export function emitSound(
  world: World,
  x: number,
  y: number,
  radius: number,
  maxLife = 0.8,
  color = 'rgba(255,200,100,.55)',
): void {
  world.sounds.push({ x, y, radius, life: maxLife, maxLife, color });
}

export function triggerAbility(world: World, name: AbilityName): boolean {
  if (world.state !== STATE.PLAY) return false;
  if (world.cooldowns[name] > 0) return false;

  switch (name) {
    case 'box':
      return doBox(world);
    case 'knock':
      return doKnock(world);
    case 'decoy':
      return doDecoy(world);
    case 'takedown':
      return doTakedown(world);
    case 'hack':
      return doHack(world);
  }
}

function doHack(world: World): boolean {
  const target = findHackTarget(world);
  if (!target) {
    // Tiny cooldown so spamming H doesn't feel free.
    world.cooldowns.hack = COOLDOWN_MAX.hack * 0.25;
    return false;
  }
  hackCamera(target);
  audio.play('decoyPing'); // borrow the cyan ping for now — distinct enough from knock
  // Cyan sparks shooting from the player toward the camera as flavour.
  const dx = target.x - world.player.x;
  const dy = target.y - world.player.y;
  const dir = Math.atan2(dy, dx);
  spawnSparks(world, target.x, target.y, dir + Math.PI, 8);
  world.cooldowns.hack = COOLDOWN_MAX.hack;
  return true;
}

function doBox(world: World): boolean {
  const { player } = world;
  if (player.inBox && world.activeBox) {
    // Step out: drop the box at the player position
    player.inBox = false;
    world.activeBox.x = player.x;
    world.activeBox.y = player.y;
    audio.play('box');
  } else if (world.activeBox && dist(player, world.activeBox) < 26) {
    // Climb in
    player.inBox = true;
    audio.play('box');
  } else {
    // Spawn a new empty box ahead. Discard a previous box if it held a body
    // (player can place a fresh empty one).
    if (world.activeBox && world.activeBox.body) world.activeBox = null;
    const bx = player.x + Math.cos(player.dir) * 18;
    const by = player.y + Math.sin(player.dir) * 18;
    if (!collidesWalls(world, bx, by, 14)) {
      world.activeBox = { x: bx, y: by, body: null };
      audio.play('box');
    }
  }
  world.cooldowns.box = COOLDOWN_MAX.box;
  return true;
}

function doKnock(world: World): boolean {
  const { player } = world;
  const kx = player.x + Math.cos(player.dir) * 22;
  const ky = player.y + Math.sin(player.dir) * 22;
  emitSound(world, kx, ky, 180, 0.6, 'rgba(255,200,80,.6)');
  audio.play('knock');
  spawnSparks(world, kx, ky, player.dir);
  recordEvent(world, 'knock');
  world.cooldowns.knock = COOLDOWN_MAX.knock;
  return true;
}

function doDecoy(world: World): boolean {
  const { player } = world;
  const speed = 380;
  world.decoys.push({
    x: player.x,
    y: player.y,
    vx: Math.cos(player.dir) * speed,
    vy: Math.sin(player.dir) * speed,
    flightT: 0.5,
    life: 4,
    pulseT: 0,
    landed: false,
  });
  audio.play('decoyThrow');
  recordEvent(world, 'decoy');
  world.cooldowns.decoy = COOLDOWN_MAX.decoy;
  return true;
}

function doTakedown(world: World): boolean {
  const { player } = world;

  // Takedown is impossible while inside the box — pop the box first.
  if (player.inBox) {
    world.cooldowns.takedown = COOLDOWN_MAX.takedown * 0.4;
    return false;
  }

  // 1) Stealth K.O. — only from behind. The findTakedownTarget helper
  // encapsulates the rules so the renderer can highlight the same target.
  const opp = findTakedownTarget(world);
  if (opp) {
    const g = opp.guard;
    g.ko = true;
    emitSound(world, g.x, g.y, 80, 0.4, 'rgba(255,80,80,.4)');
    audio.play('ko');
    spawnDust(world, g.x, g.y, 14);
    recordEvent(world, 'ko');
    world.cooldowns.takedown = COOLDOWN_MAX.takedown;
    return true;
  }

  // 2) Stash a body in the active empty box (player + body + box overlap).
  if (world.activeBox && !world.activeBox.body) {
    for (const g of world.guards) {
      if (
        g.ko &&
        !g.hiddenInBox &&
        dist(g, world.activeBox) < 30 &&
        dist(player, g) < TAKEDOWN_REACH
      ) {
        g.hiddenInBox = true;
        world.activeBox.body = g;
        recordEvent(world, 'bodyHidden');
        world.cooldowns.takedown = COOLDOWN_MAX.takedown;
        return true;
      }
    }
  }

  // No-op: minor cooldown so spam doesn't bypass timing
  world.cooldowns.takedown = COOLDOWN_MAX.takedown * 0.4;
  return false;
}

export function updateAbilities(world: World, dt: number): void {
  for (const k of Object.keys(world.cooldowns) as AbilityName[]) {
    world.cooldowns[k] = Math.max(0, world.cooldowns[k] - dt);
  }

  // Decoys: fly until they land, then ping at intervals
  for (let i = world.decoys.length - 1; i >= 0; i--) {
    const d = world.decoys[i]!;
    d.life -= dt;
    if (!d.landed) {
      d.flightT -= dt;
      const nx = d.x + d.vx * dt;
      const ny = d.y + d.vy * dt;
      // Cyan trail behind the comet
      spawnDecoyTrail(world, d.x, d.y);
      if (collidesWalls(world, nx, ny, 4) || d.flightT <= 0) {
        d.landed = true;
        d.vx = 0;
        d.vy = 0;
        emitSound(world, d.x, d.y, 60, 0.4, 'rgba(80,200,255,.5)');
        spawnDust(world, d.x, d.y, 6);
      } else {
        d.x = nx;
        d.y = ny;
      }
    } else {
      d.pulseT -= dt;
      if (d.pulseT <= 0) {
        d.pulseT = 0.7;
        emitSound(world, d.x, d.y, 200, 0.7, 'rgba(80,200,255,.5)');
        audio.play('decoyPing');
      }
    }
    if (d.life <= 0) world.decoys.splice(i, 1);
  }

  // Sound rings decay
  for (let i = world.sounds.length - 1; i >= 0; i--) {
    world.sounds[i]!.life -= dt;
    if (world.sounds[i]!.life <= 0) world.sounds.splice(i, 1);
  }
}
